import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API, videoCategories, extractVideoId } from './constants';

export default function VideosTab({ videos, onRefresh }) {
  const [editingVideo, setEditingVideo] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    video_id: '',
    category: 'news',
    thumbnail_url: ''
  });

  const openDialog = (video = null) => {
    if (video) {
      setEditingVideo(video);
      setForm({
        title: video.title,
        description: video.description,
        video_id: video.video_id,
        category: video.category,
        thumbnail_url: video.thumbnail_url || ''
      });
    } else {
      setEditingVideo(null);
      setForm({ title: '', description: '', video_id: '', category: 'news', thumbnail_url: '' });
    }
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.title || !form.video_id) {
      toast.error('Please fill in title and video ID/URL');
      return;
    }
    try {
      const videoId = extractVideoId(form.video_id);
      const data = {
        ...form,
        video_id: videoId,
        thumbnail_url: form.thumbnail_url || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
      };
      if (editingVideo) {
        await axios.put(`${API}/admin/videos/${editingVideo.id}`, data);
        toast.success('Video updated');
      } else {
        await axios.post(`${API}/admin/videos`, data);
        toast.success('Video added');
      }
      setShowDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to save video');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/admin/videos/${id}`);
      toast.success('Video deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Videos ({videos.length})</CardTitle>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="gap-2">
              <Plus className="w-4 h-4" /> Add Video
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingVideo ? 'Edit Video' : 'Add Video'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>Video ID or URL *</Label>
                <Input value={form.video_id} onChange={(e) => setForm({ ...form, video_id: e.target.value })} placeholder="YouTube URL or Video ID" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {videoCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Thumbnail URL (optional)</Label>
                <Input value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} />
              </div>
              <Button onClick={save} className="w-full">{editingVideo ? 'Update' : 'Add'} Video</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {videos.map((video) => (
            <div key={video.id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              <img src={video.thumbnail_url || `https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`} alt={video.title} className="w-24 h-14 object-cover rounded" />
              <div className="flex-1">
                <h4 className="font-bold">{video.title}</h4>
                <p className="text-sm text-muted-foreground">{video.category} • {video.video_id}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openDialog(video)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="destructive" size="sm" onClick={() => deleteItem(video.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
